import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import Card from "react-bootstrap/Card";
import {Column, DataGrid, Paging, Scrolling} from "devextreme-react/data-grid";
import apiClient from "../../services/axios";
import LoaderService from "../../RetroAssets.System/LoaderService";

// Receivables and arrears aging. Nexus s1.9.
//
// GET /api/Property/GetArrears takes NO parameter - no year and no as-at date.
// There is no selector on this screen for the same reason there is no parameter
// on the route: arrears is a STOCK, not a period measure, and the data cannot
// honestly answer an as-at question. docs/ARREARS-DEFINITION-01.md Part 4.4 has
// the measurements. The screen states the date it is as at instead of offering
// to change it.
//
//   [ {"Data": [ ...rows... ]}, {"Message": "..."}, {"AsAt": "yyyy-MM-dd"} ]
//
// Positional and double encoded, like every other response in this API: the
// body is a JSON string holding the payload. One flagged row set, not two.
//
//   RowType 'Summary'  EXACTLY FIVE rows, one per aging bucket, always present -
//                      a bucket holding nothing arrives as ItemCount 0 and 0.00,
//                      never as a missing row. These drive the strip AND the
//                      headline, so the two cannot disagree.
//   RowType 'Item'     one outstanding receivable each, oldest first.
//
// THIS SCREEN BUILDS NO REMINDER, NO CHASING AND NO SEND ACTION, and that is a
// data finding rather than a missing feature. Across all 193 tenancy contracts
// TenantEmail is blank on 92.23 % and TenantContact on 86.01 %; on the live
// arrears set both are blank on every row. The two contact columns are rendered
// anyway, blank, because hiding them would hide the reason the list cannot be
// actioned.

// Grid sizing. Same mechanism as the Occupancy and NOI screens - DevExtreme is
// handed a pixel height, and every variable that feeds it is measured at layout
// time rather than assumed. The constants below are used only before the grid
// exists, during a load or an error, and each is a named stylesheet rule.
const MIN_GRID_HEIGHT = 120;
const NOTE_MARGIN = 8;          // footnote mt-2
const CARD_PADDING = 16;        // Card p-3, bottom half
const WRAPPER_PADDING = 16;     // div.main-wrapper p-3, bottom half
const CONTENT_TAIL = 56;        // .content-wrapper padding-bottom, global.scss:10-13

// A compact panel, not a paginated grid: five rows is the expected size. So the
// grid is capped at the height its own rows actually need and only then by the
// space available, instead of reserving the whole viewport and drawing several
// hundred pixels of empty banding under four rows. Fallbacks are used only
// before a row has drawn.
const FALLBACK_HEADER_HEIGHT = 40;
const FALLBACK_ROW_HEIGHT = 36;
const GRID_BORDERS = 2;

const isPresent = value => value !== null && value !== undefined && value !== "";

// Every figure the endpoint returns is AED, converted inside PropertyArrears.txt
// at the rate stored on each transaction. One of the four live receivables is in
// EURO, so nothing on this screen may fall back to a raw balance.
const money = value => (isPresent(value)
    ? Number(value).toLocaleString("en-AE", {minimumFractionDigits: 2, maximumFractionDigits: 2})
    : "—");

const countText = value => (isPresent(value) ? Number(value).toLocaleString() : "—");

// The transaction date arrives as an ISO instant - "2026-07-20T00:00:00". Sliced
// rather than passed through new Date(): the browser reads a bare date as UTC
// midnight and renders the day before in any negative offset, and this screen is
// read in Gulf Standard Time. The string is already the calendar date the server
// aged on, so there is nothing to convert.
const dateText = value => (isPresent(value) ? String(value).slice(0, 10) : "—");

// DaysOutstanding IS SIGNED AND MAY BE NULL, and neither may render as a plain
// zero. Negative means the transaction date has not arrived - two of the four
// live receivables are dated a week ahead - and NULL means the row carries no
// date at all. Both sit in Not yet due and neither is overdue.
const daysText = value => {
    if (!isPresent(value)) return "—";
    const days = Number(value);
    if (days < 0) return "in " + Math.abs(days);
    return String(days);
};

// The five buckets, in the order a strip is read. The order is the server's
// BucketOrder; this list exists so the strip still renders its five slots while
// a read is in flight or has failed, and so a bucket the server stopped sending
// would show up as an empty slot rather than silently shortening the strip.
const BUCKET_ORDER = [1, 2, 3, 4, 5];

function Arrears() {
    const [rows, setRows] = useState([]);
    const [asAt, setAsAt] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [height, setHeight] = useState(MIN_GRID_HEIGHT);

    const gridAreaRef = useRef(null);
    const noteRef = useRef(null);
    // Smallest height computed since the last deliberate chrome change. The
    // footnote rewraps when the page scrollbar appears or disappears, and its
    // height feeds the next measurement, so a grid sized to exactly fill the
    // viewport can otherwise flip between two values forever.
    const floorRef = useRef(Infinity);

    const summaryRows = useMemo(
        () => rows.filter(r => r["RowType"] === "Summary"),
        [rows]);
    // ECO_ID is the grid key everywhere in this application because Property.Code
    // is not unique - it repeats seven times and across companies. Here the key is
    // finer still: one property can carry two outstanding rows, and two of the
    // four live ones sit on the same property. TransactionECO_ID is the key, and a
    // row without one is dropped before it can draw as a blank full-height band.
    const itemRows = useMemo(
        () => rows.filter(r => r["RowType"] === "Item" && isPresent(r["TransactionECO_ID"])),
        [rows]);

    // The strip, driven from the server's five Summary rows. A slot with no
    // matching row still renders, at 0.00 - AN EMPTY 90+ IS A FINDING, NOT AN
    // ABSENCE, and omitting it would read as "we did not look".
    const buckets = useMemo(() => BUCKET_ORDER.map(order => {
        const found = summaryRows.find(r => Number(r["BucketOrder"]) === order);
        return {
            order: order,
            name: found && isPresent(found["Bucket"]) ? found["Bucket"] : "",
            count: found ? Number(found["ItemCount"]) : 0,
            amount: found ? Number(found["AmountOutstandingAED"]) : 0
        };
    }), [summaryRows]);

    // Headline totals come from the buckets, NOT from re-summing the items, so
    // the three figures at the top and the five figures below them are the same
    // arithmetic and cannot drift apart. Bucket 1 is Not yet due; 2 to 5 are the
    // aged buckets, which is exactly what "overdue" means here.
    const totals = useMemo(() => {
        const sum = (list, field) => list.reduce((acc, b) => acc + b[field], 0);
        const overdueBuckets = buckets.filter(b => b.order > 1);
        const notYetDue = buckets.filter(b => b.order === 1);
        return {
            total: sum(buckets, "amount"),
            totalCount: sum(buckets, "count"),
            overdue: sum(overdueBuckets, "amount"),
            overdueCount: sum(overdueBuckets, "count"),
            notYetDue: sum(notYetDue, "amount"),
            notYetDueCount: sum(notYetDue, "count")
        };
    }, [buckets]);

    // The finding the strip is there to make visible, stated in words rather than
    // left to be inferred from a row of zeros. Every part of it is derived from
    // the data and none of it is hardcoded - the buckets it names as empty are
    // the ones that ARE empty, so the sentence rewrites itself the moment
    // something ages further instead of going quietly out of date.
    const agingFinding = useMemo(() => {
        if (loading || error || totals.totalCount === 0) return "";
        const aged = buckets.filter(b => b.order > 1 && b.count > 0);
        if (aged.length === 0)
            return "Nothing in the book is past due. Every outstanding item is dated ahead of today.";
        const oldest = aged[aged.length - 1];
        // Every bucket older than the oldest occupied one. They are empty because
        // nothing has aged that far, NOT because they were not measured, which is
        // the whole reason the server returns all five buckets including zeros.
        const beyond = buckets
            .filter(b => b.order > oldest.order && isPresent(b.name))
            .map(b => b.name.replace(" days", ""));
        if (beyond.length === 0)
            return "There are items in the oldest bucket, " + oldest.name + ".";
        const named = beyond.length === 1
            ? beyond[0]
            : beyond.slice(0, -1).join(", ") + " and " + beyond[beyond.length - 1];
        return "Nothing has aged beyond " + oldest.name + " — the " + named
            + (beyond.length === 1 ? " bucket is" : " buckets are")
            + " empty, not unmeasured.";
    }, [buckets, totals, loading, error]);

    const measureGrid = useCallback(() => {
        const area = gridAreaRef.current;
        if (!area) return;

        const viewport = document.documentElement.clientHeight;
        const scrollY = window.scrollY;

        const nav = document.getElementsByClassName("bottom-navigation")[0];
        // Fixed-bottom and overlaying the page, so its height comes off the grid
        // rather than out of the document flow. Measured, not assumed: it is
        // taller once the navbar collapses below lg.
        const navHeight = nav ? nav.offsetHeight : CONTENT_TAIL;

        // Where the grid starts, and how much page sits below its last pixel -
        // the footnote, the card's own padding and border, the wrapper's padding.
        // All read off the rendered elements at the same instant, which is what
        // makes them independent of the height the grid currently happens to
        // have, so a stale height cannot feed itself back in.
        //
        // Document scrollHeight is deliberately not used: the content wrapper
        // carries min-height: calc(100vh - 60px) (global.scss:10-13), so a short
        // page is pinned to the viewport and scrollHeight would report a tail
        // that is mostly empty space.
        const grid = area.querySelector(".dx-datagrid");
        const note = noteRef.current;
        const card = area.closest(".card");
        const wrapper = area.closest(".main-wrapper");

        let top;
        let toCardBottom;
        let toWrapperBottom;
        if (grid && card && wrapper) {
            const rect = grid.getBoundingClientRect();
            // Document relative, not viewport relative. A page that is currently
            // scrolled would otherwise feed its own scroll offset into the next
            // height and grow without bound.
            top = rect.top + scrollY;
            toCardBottom = card.getBoundingClientRect().bottom - rect.bottom;
            toWrapperBottom = wrapper.getBoundingClientRect().bottom - rect.bottom;
        } else {
            const padTop = parseFloat(window.getComputedStyle(area).paddingTop) || 0;
            top = area.getBoundingClientRect().top + scrollY + padTop;
            toCardBottom = (note ? note.offsetHeight : 0) + NOTE_MARGIN + CARD_PADDING;
            toWrapperBottom = toCardBottom + WRAPPER_PADDING;
        }

        // Two constraints, both of which have to hold. The document must not
        // outgrow the viewport, which costs the content wrapper's tail padding on
        // top of everything in the flow; and the last drawn pixel of the card must
        // clear the fixed bar, which costs the bar's height instead. The wrapper's
        // trailing padding is empty, so the bar may overlap it - the card border
        // may not.
        const reserve = Math.max(toWrapperBottom + CONTENT_TAIL, toCardBottom + navHeight);

        // Floor, not round. A half pixel of line box left over is a page
        // scrollbar, and one pixel short of the viewport is invisible.
        let available = Math.floor(viewport - top - reserve);
        available = Math.min(available, floorRef.current);
        floorRef.current = available;

        // What the rows actually need. Measured off the drawn header and the drawn
        // rows, NONE of which depends on the grid's height, so this cannot feed
        // back into itself the way a scrollHeight reading would.
        //
        // The rows are SUMMED, not multiplied by the first one's height. Rows are
        // not a uniform height: wordWrapEnabled is on, and below about 1100px a
        // long tenant name wraps to two lines while the rows around it stay at
        // one. Multiplying the tallest by the row count reserved a whole extra
        // row of empty banding under the last item at half width.
        let next = available;
        if (grid && itemRows.length > 0) {
            const headers = grid.querySelector(".dx-datagrid-headers");
            const drawn = grid.querySelectorAll(".dx-data-row");
            const headerHeight = headers ? headers.offsetHeight : FALLBACK_HEADER_HEIGHT;
            let rowsHeight = 0;
            drawn.forEach(row => { rowsHeight += row.offsetHeight; });
            // Ceil: a fraction of a line box left over on the last row is enough
            // to clip it and put a scrollbar inside the grid for no reason.
            if (drawn.length !== itemRows.length || rowsHeight === 0)
                // Not every row has drawn yet - first pass, or a re-render in
                // flight. Fall back to the estimate and let the next pass correct.
                rowsHeight = FALLBACK_ROW_HEIGHT * itemRows.length;
            // The horizontal scrollbar, when there is one, is drawn OVER the
            // bottom of the rows rather than under them, so its height has to be
            // added or the last row is clipped by exactly that much and the grid
            // grows a vertical scrollbar it does not need. Measured off the
            // element, because its thickness is a theme value.
            const hBar = grid.querySelector(".dx-scrollbar-horizontal");
            const hBarHeight = (hBar && hBar.offsetHeight > 0 && hBar.offsetWidth > 0)
                ? hBar.offsetHeight
                : 0;
            const content = headerHeight + Math.ceil(rowsHeight) + hBarHeight + GRID_BORDERS;
            next = Math.min(available, content);
        }

        next = Math.max(MIN_GRID_HEIGHT, next);
        setHeight(prev => (prev === next ? prev : next));
    }, [itemRows]);

    // No dependency list. Every state this component holds moves the chrome above
    // the grid - the headline figures, the bucket strip, the loading and error
    // bodies - and the effect settles to a fixed point after one further pass,
    // because a matching height sets no state.
    useLayoutEffect(() => {
        measureGrid();
    });

    useEffect(() => {
        const onResize = () => {
            floorRef.current = Infinity;
            measureGrid();
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [measureGrid]);

    useEffect(() => {
        // cancelled guards every setState below, so navigating away mid-request
        // cannot land an update on an unmounted component.
        let cancelled = false;
        setLoading(true);
        setError("");
        floorRef.current = Infinity;
        LoaderService.setData(true);

        apiClient
            .get("/Property/GetArrears")
            .then(response => {
                LoaderService.setData(false);
                if (cancelled) return;
                try {
                    const payload = JSON.parse(response.data);
                    const message = payload[1] ? payload[1]["Message"] : "";
                    // The as-at date is read whether the body succeeded or not -
                    // the server populates it on every path, and a point-in-time
                    // figure with no point in time beside it is worse than none.
                    setAsAt(payload[2] && isPresent(payload[2]["AsAt"]) ? payload[2]["AsAt"] : "");
                    if (message) {
                        // Business failure still arrives as HTTP 200.
                        console.log("GetArrears: " + message);
                        setRows([]);
                        setError(message);
                    } else {
                        const data = payload[0] ? payload[0]["Data"] : null;
                        setRows(Array.isArray(data) ? data : []);
                    }
                } catch (e) {
                    console.log(e);
                    setRows([]);
                    setError("Arrears data could not be read. See the browser console for details.");
                }
                setLoading(false);
            })
            .catch(err => {
                // No alert - the failure is rendered in the page body instead.
                LoaderService.setData(false);
                console.log(err);
                if (cancelled) return;
                setRows([]);
                setError("Arrears could not be loaded. See the browser console for details.");
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    // The two-line property treatment the listing and the Occupancy screen use.
    // Code is display only - it is not unique and it repeats across companies -
    // so it sits under the name and is never the key.
    const PropertyCell = props => (
        <div>
            <div className="col-12"><b>{isPresent(props.data["Name"]) ? props.data["Name"] : "—"}</b></div>
            <div className="col-12">
                {isPresent(props.data["Code"]) ? props.data["Code"] : ""}
                {props.data["PropertySold"] === true &&
                    <span className="text-secondary"> · sold</span>}
            </div>
        </div>
    );

    // Two of the four live rows have no tenant at all, and that is structural
    // rather than missing data: a short-term-rental booking has no tenancy
    // contract because the counterparty is the operator. The source is printed
    // under the name so the two are never read as the same relationship.
    const CounterpartyCell = props => (
        <div>
            <div className="col-12">
                {isPresent(props.data["CounterpartyName"]) ? props.data["CounterpartyName"] : "—"}
            </div>
            <div className="col-12 text-secondary">
                {props.data["CounterpartySource"] === "Operator"
                    ? "rental operator"
                    : (props.data["CounterpartySource"] === "Tenant" ? "tenant" : "")}
            </div>
        </div>
    );

    // BLANK IS THE ANSWER AND IT IS THE FINDING. No placeholder, no dash, no
    // hidden column: an empty cell is what 92 % of contracts hold, and it is the
    // reason nothing on this screen can be actioned.
    const ContactCell = field => props => (
        <span>{isPresent(props.data[field]) ? props.data[field] : ""}</span>
    );

    const AmountCell = props => <span>{money(props.data["AmountOutstandingAED"])}</span>;
    const DaysCell = props => <span>{daysText(props.data["DaysOutstanding"])}</span>;
    const DateCell = props => <span>{dateText(props.data["TransactionDate"])}</span>;

    const headlineCard = (heading, amount, count, note, muted) => (
        <Card className="card-light p-3 border-0 h-100">
            <div className={"fs-4 fw-bold mb-1" + (muted ? " text-secondary" : "")}>
                AED {money(amount)}
            </div>
            <div className="text-secondary text-uppercase">{heading}</div>
            <div className="text-secondary fs-7 mt-1">
                {countText(count)}{Number(count) === 1 ? " item" : " items"} · {note}
            </div>
        </Card>
    );

    const bucketCard = bucket => (
        <Card className="card-light p-3 border-0 h-100">
            <div className={"fw-bold mb-1" + (bucket.count === 0 ? " text-secondary" : "")}>
                AED {money(bucket.amount)}
            </div>
            <div className="text-secondary text-uppercase fs-7">
                {bucket.name || "—"}
            </div>
            <div className="text-secondary fs-7 mt-1">
                {countText(bucket.count)}{bucket.count === 1 ? " item" : " items"}
            </div>
        </Card>
    );

    const gridBody = () => {
        if (loading)
            return <div className="text-secondary p-3">Loading arrears&hellip;</div>;
        if (error)
            return (
                <div className="text-danger p-3" role="alert">
                    <div className="fw-bold mb-1">Arrears could not be loaded.</div>
                    <div>{error}</div>
                </div>
            );
        if (itemRows.length === 0)
            return (
                <div className="text-secondary p-3">
                    Nothing is outstanding. No uncancelled income transaction carries a balance
                    as at {isPresent(asAt) ? asAt : "today"}.
                </div>
            );
        return (
            <DataGrid
                dataSource={itemRows}
                keyExpr="TransactionECO_ID"
                height={height}
                showBorders={true}
                showRowLines={true}
                showColumnLines={true}
                columnAutoWidth={true}
                allowColumnResizing={true}
                columnResizingMode="widget"
                rowAlternationEnabled={true}
                hoverStateEnabled={true}
                wordWrapEnabled={true}
                /*  REQUIRED, not decorative. measureGrid caps the grid at the
                    height its own rows need, and DevExtreme draws those rows
                    ASYNCHRONOUSLY, after React has finished committing. The
                    layout effect therefore runs first, finds the header at 2px
                    and no .dx-data-row at all, falls back to 36px a row and
                    settles on 148px - and because that height then stops
                    changing, React never renders again and the layout effect
                    never runs again. The grid stays 148px tall with its last
                    row cut off and its fourth row not drawn at all; measured,
                    not theorised, and a forced re-measure returns 203px.
                    onContentReady is the point at which the real header and row
                    heights exist, so the cap is computed from what was drawn
                    rather than from the fallbacks. It converges in one further
                    pass: a matching height sets no state. */
                onContentReady={measureGrid}
            >
                {/* The server returns the book oldest first and there are five rows
                    of it. No paging, no filter row, no column chooser: this is a
                    panel, and the query is what keeps it general. */}
                <Paging enabled={false}/>
                {/* Nine columns do not fit below about 1250px, and the one that
                    falls off the right edge first is the amount. The scrollbar is
                    forced visible rather than left to appear on hover, so a reader
                    at half width can SEE that there is more of the row - the
                    alternative, hidingPriority, would drop columns, and the two
                    contact columns exist precisely to show a gap. The page itself
                    never scrolls sideways; this scrolls inside the grid. */}
                <Scrolling mode="standard" showScrollbar="always" useNative={false}/>
                <Column caption="Property" dataField="Name" dataType="string" minWidth={200}
                        cellRender={PropertyCell}/>
                <Column caption="Tenant / counterparty" dataField="CounterpartyName" dataType="string"
                        minWidth={170} cellRender={CounterpartyCell}/>
                <Column caption="Kind" dataField="Kind" dataType="string" width={130}/>
                <Column caption="Date" dataField="TransactionDate" dataType="string" width={110}
                        cellRender={DateCell}/>
                <Column caption="Days" dataField="DaysOutstanding" dataType="number"
                        alignment="right" width={90} cellRender={DaysCell}/>
                <Column caption="Bucket" dataField="Bucket" dataType="string" width={110}/>
                <Column caption="Outstanding (AED)" dataField="AmountOutstandingAED" dataType="number"
                        alignment="right" width={160} cellRender={AmountCell}/>
                {/* Rendered blank, never hidden. See the footnote. */}
                <Column caption="Tenant email" dataField="TenantEmail" dataType="string"
                        minWidth={150} cellRender={ContactCell("TenantEmail")}/>
                <Column caption="Tenant phone" dataField="TenantContact" dataType="string"
                        minWidth={130} cellRender={ContactCell("TenantContact")}/>
            </DataGrid>
        );
    };

    return (
        <>
            <div
                className="search-actions-header d-flex flex-wrap flex-lg-nowrap justify-content-between align-items-center px-3 pt-3 p-lg-3 bg-white border-bottom">
                <div className="d-sm-flex justify-content-between align-items-center flex-fill mb-3 mb-lg-0">
                    <h2 className="fs-5 fw-medium mb-3 mb-sm-0 flex-shrink-0 me-4">Receivables and arrears</h2>
                    {/* There is no selector here. The endpoint takes no parameter,
                        and the date is stated rather than offered. */}
                    <div className="d-flex align-items-center justify-content-sm-end flex-fill me-lg-1">
                        <span className="text-secondary flex-shrink-0">
                            As at {isPresent(asAt) ? asAt : "—"}
                        </span>
                    </div>
                </div>
            </div>
            <div className="main-wrapper p-3">
                <Card className="p-3 border-0 dashboard-border">
                    {/* Three across from sm up, and five buckets across from md
                        up, rather than stacking until xl. THIS IS A HEIGHT
                        CONSTRAINT, NOT A TASTE ONE: everything above the grid is
                        height the grid does not get, and measureGrid can only
                        shrink the grid to MIN_GRID_HEIGHT before the page starts
                        scrolling instead. Stacking the three headline cards and
                        wrapping the strip onto three rows costs ~370px, which at
                        half of a 1920 viewport overflowed the page by 34px and
                        pushed the card under the fixed bottom bar. Verified at
                        1920 and at 960: no page scrollbar at either. */}
                    <div className="row mb-3">
                        <div className="col-12 col-sm-4 mb-3 mb-sm-0">
                            {headlineCard("Total outstanding", totals.total, totals.totalCount,
                                "money owed to the portfolio", false)}
                        </div>
                        <div className="col-12 col-sm-4 mb-3 mb-sm-0">
                            {headlineCard("Of which overdue", totals.overdue, totals.overdueCount,
                                "dated on or before " + (isPresent(asAt) ? asAt : "today"), false)}
                        </div>
                        <div className="col-12 col-sm-4">
                            {headlineCard("Of which not yet due", totals.notYetDue, totals.notYetDueCount,
                                "dated ahead of " + (isPresent(asAt) ? asAt : "today"), true)}
                        </div>
                    </div>

                    <div className="row row-cols-2 row-cols-md-5 g-3 mb-2">
                        {buckets.map(bucket => (
                            <div className="col" key={bucket.order}>
                                {bucketCard(bucket)}
                            </div>
                        ))}
                    </div>
                    {isPresent(agingFinding) &&
                        <div className="text-secondary fs-7 mb-3">{agingFinding}</div>}

                    <div className="tab-content pt-1" ref={gridAreaRef}>
                        {gridBody()}
                        <div className="text-secondary fs-7 mt-2" ref={noteRef}>
                            Point in time as at {isPresent(asAt) ? asAt : "today"}, aged on the
                            transaction date and shown in AED at the rate stored on each
                            transaction. Cancelled transactions are excluded: in this system the
                            cancelled flag is the write-off marker, and every cancelled
                            outstanding row sits on a cancelled tenancy contract. Money the owner
                            owes — purchase-plan installments, loans, refunds — is a payable, not
                            an arrear, and is not shown here. An item only exists once the
                            transaction has been entered, so this measures unpaid entered
                            obligations rather than unreceived contractual rent: a late cheque
                            that was never keyed appears nowhere. Tenant email and phone are
                            blank on most rows — 92% of tenancy contracts carry no email address
                            and 86% no phone number — and that gap, not the aging, is why this
                            list cannot yet be chased from the screen.
                        </div>
                    </div>
                </Card>
            </div>
        </>
    );
}

export default Arrears;
